import React, { useState, useEffect, useMemo, useRef }  from 'react';
import { Route, NavLink, Redirect, Switch, useLocation } from 'react-router-dom';
import { DateTime } from 'luxon';

// Styles
import { makeStyles } from '@material-ui/core/styles';

// Material UI components
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Link from '@material-ui/core/Link';
import MenuList from '@material-ui/core/MenuList';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Badge from '@material-ui/core/Badge';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Avatar from '@material-ui/core/Avatar';
import Hidden from '@material-ui/core/Hidden';

// Notifications components
import Popper from '@material-ui/core/Popper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';

// Icons
import MenuIcon from '@material-ui/icons/Menu';
import NotificationsIcon from '@material-ui/icons/Notifications';
import AccountIcon from '@material-ui/icons/AccountCircle';

// Layout Components
import AppDrawer from 'app/components/AppDrawer';
import ProfileMenu from 'app/components/ProfileMenu';

// Pages
import Unauthorized from 'app/pages/Unauthorized';
import Support from 'app/pages/Support';
import PageNotFound from 'app/pages/PageNotFound';
import BetaFeature from 'app/pages/BetaFeature';

import Feed from 'app/pages/Feed';
import Profile from 'app/pages/Profile';
import Directory from 'app/pages/Directory';
import Articles from 'app/pages/Articles';
import ViewArticle from 'app/pages/ViewArticle';
import Membership from 'app/pages/Membership';
import Drive from 'app/pages/Drive';

import Events from 'app/pages/Events';
import ViewEvent from 'app/pages/ViewEvent';
import GuestForm from 'app/pages/PublicGuestForm';
import Invitations from 'app/pages/Invitations';
import Tickets from 'app/pages/Tickets';
import Guests from 'app/pages/Guests';

import Proposals from 'app/pages/Proposals';
import ViewProposals from 'app/pages/ViewProposals';
import Recommendations from 'app/pages/Recommendations';
import ViewRecommendations from 'app/pages/ViewRecommendations';
import Voting from 'app/pages/Voting';

import Forms from 'app/pages/Forms';
import ViewForm from 'app/pages/ViewForm';
import Form from 'app/pages/PublicForm';
//import UserProposals from 'app/pages/UserProposals';
//import UserRecommendations from 'app/pages/UserRecommendations';
//import UserVoting from 'app/pages/UserVoting';

import AdminDashboard from 'app/pages/admin/Dashboard';
import AdminAccessRequests from 'app/pages/admin/AccessRequestPage';
import AccessRequestDetails from 'app/pages/admin/AccessRequestDetails';
import ResignationRequestDetails from 'app/pages/admin/ResignationRequestDetails';
import ResignationRequestsPage from 'app/pages/admin/ResignationRequestsPage';
import AdminUsers from 'app/pages/admin/Users';
import AdminEditUser from 'app/pages/admin/EditUser';
import AdminGroups from 'app/pages/admin/Groups';
import AdminLevels from 'app/pages/admin/MembershipLevels';
import AdminEmails from 'app/pages/admin/Emails';
import AdminEditEmail from 'app/pages/admin/EditEmail';
import AdminViewEmail from 'app/pages/admin/ViewEmail';
import AdminEvents from 'app/pages/admin/Events';
import AdminEditEvent from 'app/pages/admin/EditEvent';
import AdminNotifications from 'app/pages/admin/Notifications';
import AdminArticles from 'app/pages/admin/Articles';
import AdminEditArticle from 'app/pages/admin/EditArticle';
import AdminElections from 'app/pages/admin/Elections';
import AdminEditElection from 'app/pages/admin/EditElection';
import AdminForms from 'app/pages/admin/Forms';
import AdminEditForm from 'app/pages/admin/EditForm';
import AdminDrive from 'app/pages/admin/Drive';

// Accounting
import AdminAccountingDashboard from 'app/pages/admin/accounting/Dashboard';
import AdminAccountingStripeOnboarding from 'app/pages/admin/accounting/StripeOnboarding';
import AdminAccountingInvoices from 'app/pages/admin/accounting/Invoices';
import AdminAccountingEditInvoice from 'app/pages/admin/accounting/EditInvoice';
import AdminAccountingViewInvoice from 'app/pages/admin/accounting/ViewInvoice';
import AdminAccountingPayments from 'app/pages/admin/accounting/Payments';
import AdminAccountingAccounts from 'app/pages/admin/accounting/Accounts';
import AdminAccountingSettings from 'app/pages/admin/accounting/Settings';

import AdminPages from 'app/pages/admin/Pages';
import AdminEditPage from 'app/pages/admin/EditPage';

import AdminAnalytics from 'app/pages/admin/Analytics';
import AdminSettings from 'app/pages/admin/Settings';

import ScrollToTop from 'app/components/ScrollToTop';
import Page from 'app/components/pages/Page';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    grow: {
        flexGrow: 1,
    },
    appFrame: {
        zIndex: 1,
        display: 'flex',
    },
    appBar: {
        width: `calc(100% - ${theme.width.navigation}px)`,
        marginLeft: theme.width.navigation,

        [theme.breakpoints.down('sm')]: {
            width: `100%`,
            marginLeft: 0,
        }
    },
    toolbar: theme.mixins.toolbar,
    main: {
        position: 'relative',
        flexGrow: 1,
        width: `calc(100% - ${theme.width.navigation}px)`,

        [theme.breakpoints.down('sm')]: {
            width: `100%`,
        }
    },
    content: {
        position: 'relative'
    },
    // Notification styles
    popper: {
        zIndex: theme.zIndex.modal
    },
    notificationPaper: {
        width: '380px',
        maxWidth: '100%',
        maxHeight: '480px',
        overflowY: 'scroll'
    },
    grid: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        marginLeft: '0px',
        marginRight: '0px',
        paddingLeft: '0px',
        paddingRight: '0px'
    },
    profileButton : {
        marginLeft: theme.spacing(1),
        cursor: 'pointer'
    },

}));

export default function ClubLayout(props) {

    const [ notifications, setNotifications ] = useState([]);

    const [ drawerOpen, setDrawerOpen ] = useState(false);
    const [ notificationsOpen, setNotificationsOpen ] = useState(false);
    const [ profileOpen, setProfileOpen ] = useState(false);

    const notificationsAnchorRef = useRef(null);
    const profileAnchorRef = useRef(null);

    const classes = useStyles();
    let location = useLocation();

    const { app } = useApp();
    const api = useApi();

    // Getter Functions ------------------------------------------

    const getNotifications = () => {
        api.get('/notifications')
            .then(res => {
                const newNotifications = res.notifications;
                setNotifications(newNotifications);

            }).catch(error => {
                console.log(error.response);
            });
    }

    useEffect(() => {
        getNotifications();
        setNotificationsOpen(false);
        setProfileOpen(false);
        setDrawerOpen(false);
    }, [location]);

    // Notifications Functions ------------------------------------------

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    const handleNotificationsMenu = () => {
        setNotificationsOpen((prevOpen) => !prevOpen)
        postReadNotifications();
    }

    const handleNotificationsClose = event => {
        if (notificationsAnchorRef.current && notificationsAnchorRef.current.contains(event.target)) {
            return;
        }

        setNotificationsOpen(false);
    };

    const handleNotificationsListKeyDown = event => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setNotificationsOpen(false);
        }
    }

    const handleProfileMenu = () => {
        setProfileOpen((prevOpen) => !prevOpen)
    }

    const handleProfileMenuClose = () => {
        setProfileOpen(false)
    }

    // Setter Functions ------------------------------------------

    const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);

    // Post functions ------------------------------------------

    const postReadNotifications = () => {
        const newNotifications = [...notifications];

        newNotifications.forEach(function(n, i) {
            newNotifications[i].read_at = DateTime.local().toString()
        });

        setNotifications(newNotifications);

        api.get('/notifications/read');
    }

    // Helper functions ------------------------------------------

    const unreadNotifications = () => {
        let count = 0;
        notifications.forEach(function(n) {
            if (n.read_at == null) count++;
        })
        return count ? count : "";
    }

    const NotificationsMenu = (
        <Popper
            anchorEl={notificationsAnchorRef.current}
            open={notificationsOpen}
            onClose={handleNotificationsClose}
            role={undefined}
            transition
            disablePortal
            className={classes.popper}
            placement='bottom-end'
            >
            {({ TransitionProps, placement }) => (
                <Grow {...TransitionProps} style={{ transformOrigin: 'right top'}}>
                    <Paper className={classes.notificationPaper}>
                        <ClickAwayListener onClickAway={handleNotificationsClose}>
                            <MenuList
                                id="notifications-menu"
                                onKeyDown={handleNotificationsListKeyDown}
                                >
                                {notifications.length ? notifications.map( n => (
                                    <ListItem
                                        alignItems="flex-start"
                                        key={n.id}
                                        selected={n.read_at === null ? true : false}
                                        >
                                        <ListItemAvatar>
                                            <Avatar
                                                aria-label={n.data.from}
                                                alt={n.data.from}
                                                src={n.data.avatar}
                                                variant="rounded"
                                                >
                                                {n.data.from.charAt(0)}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText disableTypography>
                                            <Typography
                                                component="div"
                                                color="textPrimary"
                                                variant="subtitle2"
                                                >
                                                {n.data.from}
                                            </Typography>
                                            <Typography
                                                component="div"
                                                color="textSecondary"
                                                variant="body1"
                                                >
                                                to {n.data.to}
                                            </Typography>
                                            <Typography color="textPrimary" variant="body1">
                                                {n.data.message}
                                            </Typography>
                                            <Typography component="div" variant="body2" color="textSecondary">
                                                <Grid container justify="space-between" className={classes.grid}>
                                                    <Grid item>
                                                        {n.data.link_url &&
                                                            <Link component={NavLink} to={n.data.link_url}>{n.data.link_text}</Link>
                                                        }
                                                    </Grid>
                                                    <Grid item className="tr">
                                                        {DateTime.fromISO(n.created_at).toRelative()}
                                                    </Grid>
                                                </Grid>
                                            </Typography>
                                        </ListItemText>
                                    </ListItem>
                                )): (
                                    <ListItem>
                                        <ListItemText secondary="No new notifications at this time."/>
                                    </ListItem>
                                )}
                            </MenuList>
                        </ClickAwayListener>
                    </Paper>
                </Grow>
            )}
        </Popper>
    );

    // Admin Created Pages
    const userRoutes = useMemo(() => app.club.pages.map((page, i) => {
        if (! page.created_by_system) {
            return (
                <Route path={`/${page.path}`} key={i}>
                    <Page id={page.id}/>
                </Route>
            )
        }
    }), [app.club.pages]);

    return (
        <div className={classes.appFrame}>
            <AppBar
                position="fixed"
                className={classes.appBar}
                color="secondary"
                >
                <Toolbar>
                    <Hidden mdUp>
                        <IconButton
                            aria-label="Open Drawer"
                            onClick={handleDrawerToggle}
                            className={classes.menuButton}
                            color="inherit"
                            >
                            <MenuIcon />
                        </IconButton>
                    </Hidden>
                    <div className={classes.grow}></div>
                    <IconButton
                        ref={notificationsAnchorRef}
                        aria-controls={notificationsOpen ? 'notifications-menu' : undefined}
                        aria-haspopup="true"
                        onClick={handleNotificationsMenu}
                        color="inherit"
                        >
                        { unreadNotifications() ? (
                            <Badge className={classes.margin} badgeContent={unreadNotifications()} color="error">
                                <NotificationsIcon />
                            </Badge>
                        ) : (
                            <NotificationsIcon />
                        )}
                    </IconButton>
                    { (app.user.avatar) ? (
                        <Avatar
                            alt={app.user.full_name}
                            src={app.user.avatar.full_url}
                            aria-controls={profileOpen ? 'profile-menu' : undefined}
                            aria-haspopup="true"
                            onClick={handleProfileMenu}
                            ref={profileAnchorRef}
                            className={classes.profileButton}
                        />
                    ) : (
                        <IconButton
                            aria-controls={profileOpen ? 'profile-menu' : undefined}
                            aria-haspopup="true"
                            onClick={handleProfileMenu}
                            color="inherit"
                            ref={profileAnchorRef}
                            className={classes.profileButton}
                            >
                            <AccountIcon />
                        </IconButton>
                    )}
                </Toolbar>
            </AppBar>
            {NotificationsMenu}
            <ProfileMenu open={profileOpen} anchorRef={profileAnchorRef} onClose={handleProfileMenuClose} />
            <AppDrawer open={drawerOpen} handleClose={handleDrawerClose} />
            <main className={classes.main}>
                <div className={classes.toolbar} />
                <div className={classes.content}>
                    <Switch>
                        <Route path="/profile">
                            <Profile />
                        </Route>
                        <Route path="/dashboard" exact>
                            <Feed />
                        </Route>
                        <Route path="/unauthorized" exact>
                            <Unauthorized />
                        </Route>
                        <Route path="/support" exact>
                            <Support />
                        </Route>
                        <Route path="/directory" exact>
                            <Directory />
                        </Route>
                        <Route path="/articles" exact>
                            <Articles />
                        </Route>
                        <Route path="/articles/:id" exact>
                            <ViewArticle />
                        </Route>
                        <Route path="/membership" exact>
                            <Membership />
                        </Route>
                        <Route path="/drive" exact>
                            <Drive />
                        </Route>
                        <Route path="/events" exact>
                            <Events />
                        </Route>
                        <Route path="/events/:id" >
                            <ViewEvent />
                        </Route>
                        <Route path="/event-guest-form" exact>
                            <GuestForm />
                        </Route>
                        <Route path="/invitations">
                            <Invitations />
                        </Route>
                        <Route path="/tickets" exact>
                            <Tickets/>
                        </Route>
                        <Route path="/guests" exact>
                            <Guests/>
                        </Route>
                        <Route path="/proposals" exact>
                            <Proposals />
                        </Route>
                        <Route path="/elections/:id/proposal" exact>
                            <ViewProposals />
                        </Route>
                        <Route path="/recommendations" exact>
                            <Recommendations />
                        </Route>
                        <Route path="/elections/:id/recommendation" exact>
                            <ViewRecommendations />
                        </Route>
                        <Route path="/voting" exact>
                            <Voting />
                        </Route>
                        <Route path="/forms" exact>
                            <Forms />
                        </Route>
                        <Route path="/forms/:id" exact>
                            <ViewForm />
                        </Route>
                        <Route path={'/form/:hash'} exact>
                            <Form/>
                        </Route>
                        <Route path="/admin/dashboard" exact>
                            <AdminDashboard />
                        </Route>
                        <Route path="/admin/access-requests" exact>
                            <AdminAccessRequests />
                        </Route>
                        <Route path="/admin/access-requests/:id">
                            <AccessRequestDetails />
                        </Route>
                        <Route path="/admin/resignation-requests" exact>
                            <ResignationRequestsPage />
                        </Route>
                        <Route path="/admin/resignation-requests/:id">
                            <ResignationRequestDetails />
                        </Route>
                        <Route path="/admin/users" exact>
                            <AdminUsers />
                        </Route>
                        <Route path="/admin/users/:id">
                            <AdminEditUser />
                        </Route>
                        <Route path="/admin/groups" exact>
                            <AdminGroups />
                        </Route>
                        <Route path="/admin/levels" exact>
                            <AdminLevels />
                        </Route>
                        <Route path="/admin/emails" exact>
                            <AdminEmails />
                        </Route>
                        <Route path="/admin/emails/:id" exact>
                            <AdminEditEmail/>
                        </Route>
                        <Route path="/admin/emails/view/:id" exact>
                            <AdminViewEmail />
                        </Route>
                        <Route path="/admin/events" exact>
                            <AdminEvents />
                        </Route>
                        <Route path="/admin/events/:id" >
                            <AdminEditEvent />
                        </Route>
                        <Route path="/admin/notifications" exact>
                            <AdminNotifications />
                        </Route>
                        <Route path="/admin/articles" exact>
                            <AdminArticles />
                        </Route>
                        <Route path="/admin/articles/:id">
                            <AdminEditArticle />
                        </Route>
                        <Route path="/admin/elections" exact>
                            <AdminElections />
                        </Route>
                        <Route path="/admin/elections/:id" >
                            <AdminEditElection />
                        </Route>
                        <Route path="/admin/forms" exact>
                            <AdminForms />
                        </Route>
                        <Route path="/admin/forms/:id">
                            <AdminEditForm />
                        </Route>
                        <Route path="/admin/drive" exact>
                            <AdminDrive />
                        </Route>
                        <Route path='/admin/accounting/dashboard' exact>
                            <AdminAccountingDashboard />
                        </Route>
                        <Route path='/admin/accounting/stripe/onboarding' exact>
                            <AdminAccountingStripeOnboarding />
                        </Route>
                        <Route path="/admin/accounting/invoices/:id" exact>
                            <AdminAccountingEditInvoice />
                        </Route>
                        <Route path="/admin/accounting/invoices/view/:id" exact>
                            <AdminAccountingViewInvoice />
                        </Route>
                        <Route path="/admin/accounting/payments" exact>
                            <AdminAccountingPayments />
                        </Route>
                        <Route path="/admin/accounting/invoices" exact>
                            <AdminAccountingInvoices />
                        </Route>
                        <Route path='/admin/accounting/accounts' exact>
                            <AdminAccountingAccounts />
                        </Route>
                        <Route path='/admin/accounting/settings' exact>
                            <AdminAccountingSettings />
                        </Route>
                        <Route path="/admin/pages" exact>
                            <AdminPages/>
                        </Route>
                        <Route path="/admin/pages/:id">
                            <AdminEditPage />
                        </Route>
                        <Route path="/admin/settings">
                            <AdminSettings/>
                        </Route>
                        {userRoutes}
                        <Route path='/404'>
                            <PageNotFound />
                        </Route>
                        <Redirect exact from="/" to={`/${app.club.pages[0].path}`} />
                        <Redirect from="/login" to={`/${app.club.pages[0].path}`} />
                        <Redirect exact from="/register" to={`/${app.club.pages[0].path}`} />
                        <Redirect exact from="/password/reset" to={`/${app.club.pages[0].path}`} />
                        <Redirect to="/404" />
                    </Switch>
                </div>
            </main>
            <ScrollToTop/>
        </div>
    )
}
